import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Compass, Users, Sparkles, ArrowUpRight, Quote } from "lucide-react";
import useSeo from "@/hooks/useSeo";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

export default function AboutPage() {
  useSeo({
    title: "About ConnectHER",
    description:
      "ConnectHER was founded by Devyne Harris to connect people, businesses, and communities to the support they need — through a trusted ecosystem of financial, tax, credit, bookkeeping, and business services.",
    path: "/about",
  });

  return (
    <>
      <section className="relative pt-16 md:pt-24 pb-12" data-testid="about-hero">
        <div className="blob bg-teal/40 w-96 h-96 -top-10 -right-20 rounded-full" />
        <div className="container-app relative grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <motion.div {...fadeUp} className="eyebrow">
              About ConnectHER
            </motion.div>
            <motion.h1
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-4 text-5xl md:text-6xl leading-tight"
            >
              The connector between{" "}
              <span className="italic text-gold">
                people, purpose, and possibility.
              </span>
            </motion.h1>
            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-6 text-lg text-navy/70 leading-relaxed"
            >
              ConnectHER exists because too many founders, families, and
              nonprofits are doing extraordinary work without the financial
              systems, structure, and support they deserve. We are the bridge —
              one ecosystem of trusted services, one team, one place to go.
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="md:col-span-6"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_upgrade-web-4/artifacts/1vz8xcvl_IMG_20260624_150155.jpg"
              alt="Devyne Harris, founder of ConnectHER"
              className="rounded-[2rem] shadow-cardHover w-full h-[520px] object-cover object-center"
            />
          </motion.div>
        </div>
      </section>

      <section className="section" data-testid="about-mission">
        <div className="container-app grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow">Our mission</div>
            <h2 className="mt-3 text-4xl">Empower. Equip. Elevate.</h2>
          </div>
          <div className="md:col-span-7 text-navy/75 leading-relaxed space-y-5 text-lg">
            <p>
              We believe financial clarity is a form of freedom. When the books
              are clean, the entity is right, and the credit is rebuilt, doors
              open that were previously invisible.
            </p>
            <p>
              Through credit solutions, tax services, bookkeeping, business
              support, notary, and travel — we walk alongside our clients as a
              single, trusted partner. No more juggling five different vendors.
              No more falling through the cracks.
            </p>
            <p className="italic text-gold font-serif text-2xl">
              The Divine Connector.
            </p>
          </div>
        </div>
      </section>

      <section
        className="section bg-white"
        data-testid="about-founder-message"
      >
        <div className="container-app">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-4 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gold/20 rounded-full blur-2xl" />
                <div className="relative w-56 h-56 md:w-72 md:h-72 rounded-full overflow-hidden border-4 border-white shadow-cardHover">
                  <img
                    src="https://customer-assets.emergentagent.com/job_upgrade-web-4/artifacts/1vz8xcvl_IMG_20260624_150155.jpg"
                    alt="Devyne Harris"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
            <div className="md:col-span-8">
              <div className="eyebrow">A message from our founder</div>
              <Quote className="w-10 h-10 text-gold mt-4" />
              <p className="mt-4 text-lg md:text-xl text-navy/80 leading-relaxed font-serif italic">
                ConnectHER was built from a desire to help people access the
                support, education, and resources they need to succeed. Having
                personally navigated financial challenges, entrepreneurship,
                and the realities of balancing business and family, I
                understand the importance of having trusted guidance along the
                way.
              </p>
              <p className="mt-4 text-navy/70 leading-relaxed">
                My goal is to create a space where clients feel supported,
                informed, and empowered to take their next step with
                confidence.
              </p>
              <div className="mt-6 pt-6 border-t border-navy/10">
                <div className="font-serif text-2xl text-navy">
                  — Devyne Harris
                </div>
                <div className="text-sm uppercase tracking-widest text-gold mt-1">
                  Founder, ConnectHER
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" data-testid="about-values">
        <div className="container-app">
          <div className="eyebrow">What guides us</div>
          <h2 className="mt-3 text-4xl md:text-5xl max-w-xl">Our core values</h2>
          <div className="mt-12 grid md:grid-cols-4 gap-6">
            {[
              {
                icon: Heart,
                t: "Empathy",
                d: "We meet every client where they are — financially, emotionally, and practically.",
              },
              {
                icon: Compass,
                t: "Integrity",
                d: "We say what we mean and deliver what we promise. Every time.",
              },
              {
                icon: Users,
                t: "Community",
                d: "We grow through connection. Your wins become our community's wins.",
              },
              {
                icon: Sparkles,
                t: "Excellence",
                d: "Good is not enough. We pursue craft, clarity, and care in every detail.",
              },
            ].map((v, i) => (
              <motion.div
                key={v.t}
                {...fadeUp}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="card p-6"
              >
                <div className="w-11 h-11 rounded-2xl bg-gold/15 text-gold flex items-center justify-center mb-4">
                  <v.icon className="w-5 h-5" />
                </div>
                <h4 className="text-xl mb-2">{v.t}</h4>
                <p className="text-sm text-navy/65 leading-relaxed">{v.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" data-testid="about-cta">
        <div className="container-app">
          <div className="rounded-[2rem] bg-navy text-soft p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h3 className="text-3xl md:text-4xl text-soft max-w-lg">
                Curious if ConnectHER is the right fit?
              </h3>
              <p className="mt-3 text-soft/70 max-w-lg">
                Book a complimentary 15-minute consultation. No pressure, just
                clarity.
              </p>
            </div>
            <Link to="/book" data-testid="about-book-cta" className="btn-primary">
              Schedule Call <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
